import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import useFetch from 'use-http';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';

interface ISectionUnitReportDialogProps {
    dialogName: string;
    hideDialog: () => void;
    dialogTitle: string;
    // WTX/EASTERN/UNITLIST - matches ReportService.GetSectionUnitReportFile's own reportType switch.
    reportType: 'WTX' | 'EASTERN' | 'UNITLIST';
    defaultFilename: string;
}

const labelStyle: React.CSSProperties = { whiteSpace: 'nowrap', width: '9rem', textAlign: 'left', marginRight: '1rem' };
const fieldStyle: React.CSSProperties = { width: '65%' };

// Backs the Client | Project "WTexas Section"/"Eastern Section"/"Section/Unit List" reports -
// deployed on the report server as WTexasSection/EasternSection/SectionUnitList (see
// ReportService.GetSectionUnitReportFile for the confirmed ClNo|PrNo @Parameters shape - read
// directly off each report's own usp_*_Sections/usp_*_Units dataset pair). All 3 share this exact
// same 2-field shape, so one dialog covers all of them via the reportType prop - matches
// ClientsBilledReportDialog.tsx's own reusable-via-props pattern.
const SectionUnitReportDialog = (props: ISectionUnitReportDialogProps) => {
    const { register, errors, getValues } = useForm();
    const { get: getReport, response: reportResponse } = useFetch();
    const [isWorking, setIsWorking] = useState<boolean>(false);
    const [message, setMessage] = useState<string>('');

    const generateReport = async () => {
        const values = getValues();
        if (!values.clNo || !values.prNo) {
            setMessage('A client number and project number must be entered.');
            return;
        }

        setMessage('');
        setIsWorking(true);
        const params = new URLSearchParams({ reportType: props.reportType, clNo: values.clNo, prNo: values.prNo });
        await getReport(`/v1.0/reports/GetReport/SectionUnit?${params.toString()}`);
        setIsWorking(false);

        if (!reportResponse.ok) {
            setMessage('Unable to generate the report. Please try again.');
            return;
        }

        if (reportResponse.status === 204) {
            setMessage('No sections/units matched those filters.');
            return;
        }

        const blob = await reportResponse.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        const baseName = (values.filename || props.defaultFilename).trim().replace(/\.xlsx$/i, '');
        a.download = `${baseName || props.defaultFilename}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(blobUrl);
        props.hideDialog();
    };

    return (
        <Dialog title={props.dialogTitle} dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '25%' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <form style={{ width: '85%' }}>
                    <RHFInputBox
                        label="Client No."
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="clNo"
                        type="number"
                        register={register}
                        errors={errors}
                        style={fieldStyle}
                        bodyStyle={{ width: '100%' }}
                    />
                    <RHFInputBox
                        label="Project No."
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="prNo"
                        type="number"
                        register={register}
                        errors={errors}
                        style={fieldStyle}
                        bodyStyle={{ width: '100%' }}
                    />
                    <RHFInputBox
                        label="Filename"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="filename"
                        type="text"
                        defaultVal={props.defaultFilename}
                        register={register}
                        errors={errors}
                        style={fieldStyle}
                        bodyStyle={{ width: '100%' }}
                    />
                </form>
            </div>
            <div className="form-row" style={{ marginTop: '2rem', justifyContent: 'center' }}>
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text={isWorking ? 'Generating...' : 'Generate'}
                    loading={isWorking}
                    onClick={() => generateReport()}
                />
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text="Cancel"
                    onClick={() => props.hideDialog()}
                />
            </div>
            {!!message && (
                <div style={{ color: 'red', textAlign: 'center', marginTop: '0.5rem' }}>{message}</div>
            )}
        </Dialog>
    );
};

export default SectionUnitReportDialog;



// WEBPACK FOOTER //
// ./src/components/client-project/dialogs/SectionUnitReportDialog.tsx